import { useContext, useEffect, useState } from "react";
import { BASEURL, HEADERS } from "components/Utils/Constants";
import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";

import AbortController from "abort-controller";

import { absolute } from "api";
import { useQuery } from "@apollo/client";
import { GRANULARITY } from "components/QueryBuilder/QueryBuilderQueries";

import {
  useDeepCompare,
  hasMetrics,
  showHiddenMetrics,
  showHiddenDimensions,
  environment,
} from "./Utils/QueryBuilderUtils";

let abortController = new AbortController();

const QueryDataRequest = (props) => {
  const { state, dispatch } = useContext(QueryBuilderContext);
  const tenantSlug = localStorage.defaultTenantSlug;

  const [granularityOptions, setGranularityOptions] = useState([]);

  const { data: granularityData, error: granularityError } = useQuery(
    GRANULARITY,
    {
      skip: !hasMetrics(state),
      variables: {
        metricIds: state && state.query && state.query.metrics,
        project_id: props.projectId,
        tenantID: parseInt(localStorage.defaultTenantID),
        env: environment(),
        hiddenMetrics: showHiddenMetrics,
        showHiddenDimensions: showHiddenDimensions,
      },
      onCompleted: () => {
        setGranularityOptions((granularityData && granularityData.meta) || []);
        dispatch({
          type: "SET-GRANULARITY-OPTIONS",
          payload: (granularityData && granularityData.meta) || [],
        });
      },
      onError: () => {
        alert("Couldn't load Granularity: " + granularityError);
      },
      fetchPolicy: "network-only",
    }
  );

  const fetchData = async () => {
    // Abort new request
    abortController.abort();
    abortController = new AbortController();

    dispatch({
      type: "SET-LOADING-DATA",
      payload: true,
    });

    try {
      const endpoint = `${BASEURL}/dashboard/${tenantSlug}/query-builder?project_id=${
        props.projectId
      }&env=${environment()}&hiddenMetrics=${showHiddenMetrics}&hiddenDimensions=${showHiddenDimensions}`;
      // Enable insights
      // const endpoint = `${BASEURL}/dashboard/${tenantSlug}/query-builder?project_id=${
      //   props.projectId
      // }&env=${environment()}&insights=true&hiddenMetrics=${showHiddenMetrics}&query=`;

      // remove saveQuery options not needed for the request
      state.query.savedQuery && delete state.query.savedQuery;

      const query = JSON.stringify({ query: state.query });

      const url = `${endpoint}&format=json`;

      let response = await fetch(absolute(url), {
        method: "POST",
        headers: HEADERS(window.localStorage.token),
        body: query,
        signal: abortController.signal,
      });

      if (!response.ok) {
        dispatch({
          type: "SET-LOADING-DATA",
          payload: false,
        });

        dispatch({
          type: "SET-LOADING-DATA-ERROR",
          payload: true,
        });
      } else {
        let data = await response.json().then(async (result) => {
          // if the loadPredictions is enabled
          if (state.loadPredictions) await fetchPredictions();
          return result;
        });

        dispatch({
          type: "SET-DATA",
          payload: data,
        });

        dispatch({
          type: "SET-LOADING-DATA",
          payload: false,
        });

        return data;
      }
    } catch (e) {
      if (e.name === "AbortError") {
        console.log(e.message);
        return;
      }
      throw e;
    }
  };

  /**
   * Request the predictions based in the current QB query
   * @returns
   */
  const fetchPredictions = async () => {
    // in the case this being called in the wrong chart type.. ignore the request
    // FIXME: Do not request predictions for not allowed chart types
    if (
      ["table", "pivot-table", "bar-chart-stacked"].indexOf(
        state.queryOptions.chartType
      ) > -1
    ) {
      return;
    }
    // Abort new request
    abortController.abort();
    abortController = new AbortController();

    dispatch({
      type: "SET-LOADING-DATA",
      payload: true,
    });

    try {
      const endpoint = `${BASEURL}/dashboard/${tenantSlug}/predictions3?project_id=${
        props.projectId
      }&env=${environment()}&hiddenMetrics=${showHiddenMetrics}&hiddenDimensions=${showHiddenDimensions}&predictionRange=${
        state.predictionRange.value
      }&query=`;

      const params = JSON.stringify(state.query);
      const url = `${endpoint}${params}&format=json`;

      let response = await fetch(absolute(url), {
        method: "GET",
        headers: HEADERS(window.localStorage.token),
        signal: abortController.signal,
      });

      if (!response.ok) {
        dispatch({
          type: "SET-LOADING-DATA",
          payload: false,
        });

        dispatch({
          type: "SET-LOADING-DATA-ERROR",
          payload: true,
        });
      } else {
        let data = await response.json();

        dispatch({
          type: "SET-PREDICTIONS",
          payload: data.map((e) => ({
            ...e,
            timestamp: e.timestamp,
          })),
        });

        dispatch({
          type: "SET-LOADING-DATA",
          payload: false,
        });

        return data;
      }
    } catch (e) {
      if (e.name === "AbortError") {
        console.log(e.message);
        return;
      }
      throw e;
    }
  };

  useDeepCompare(() => {
    if (granularityOptions.length > 0) {
      fetchData();
    }
  }, [granularityOptions]);

  /**
   * Load the predictions when the handler is set
   */
  useEffect(() => {
    state.loadPredictions && fetchPredictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loadPredictions]);

  useEffect(() => {
    if (granularityOptions.length > 0 && hasMetrics(state)) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.query]);

  return null;
};

export default QueryDataRequest;
